// Lazy, guarded access to @capacitor/core.
//
// Why this exists: libs/core is consumed by both browser apps (which need
// Capacitor at runtime) and the Node-based crm-api (which does not — the
// server only pulls libs/core for pure utilities, but barrel re-exports
// drag in ApiService/AuthService). A top-level `import` of '@capacitor/core'
// causes Node to eagerly resolve the module, and `pnpm deploy --prod`
// prunes it from the API's node_modules — producing a MODULE_NOT_FOUND
// crash on boot.
//
// This shim defers the `require` to first call AND skips it entirely when
// `window` is undefined (Node), so the server never resolves the module.
// Web bundlers (Vite/webpack) still see the literal `require('@capacitor/core')`
// in the function body and bundle the dependency normally for browser builds.
//
// Type-only re-exports below are erased at compile time, so they emit no
// runtime require.

export type { HttpResponse } from '@capacitor/core';
import type { HttpOptions, HttpResponse } from '@capacitor/core';

interface CapacitorLike {
	isNativePlatform: () => boolean;
	getPlatform: () => string;
}

interface CapacitorHttpLike {
	request: (opts: HttpOptions) => Promise<HttpResponse>;
	post: (opts: HttpOptions) => Promise<HttpResponse>;
	get: (opts: HttpOptions) => Promise<HttpResponse>;
}

let _loaded = false;
let _capacitor: CapacitorLike | null = null;
let _capacitorHttp: CapacitorHttpLike | null = null;

function _load(): void {
	if (_loaded) return;
	_loaded = true;

	// Skip on non-browser runtimes (Node/SSR). Capacitor is meaningless there
	// and the package may not be installed in pruned production bundles.
	if (typeof window === 'undefined') return;

	try {
		// eslint-disable-next-line @typescript-eslint/no-var-requires
		const mod = require('@capacitor/core');
		_capacitor = (mod?.Capacitor as CapacitorLike) ?? null;
		_capacitorHttp = (mod?.CapacitorHttp as CapacitorHttpLike) ?? null;
	} catch {
		// Module not present in this runtime — leave both null.
	}
}

export const Capacitor: CapacitorLike = {
	isNativePlatform: (): boolean => {
		_load();
		return _capacitor?.isNativePlatform() ?? false;
	},
	getPlatform: (): string => {
		_load();
		return _capacitor?.getPlatform() ?? 'web';
	},
};

export const CapacitorHttp: CapacitorHttpLike = {
	request: async (opts) => {
		_load();
		if (_capacitorHttp == null) {
			throw new Error('CapacitorHttp is not available in this runtime');
		}
		return _capacitorHttp.request(opts);
	},
	post: async (opts) => {
		_load();
		if (_capacitorHttp == null) {
			throw new Error('CapacitorHttp is not available in this runtime');
		}
		return _capacitorHttp.post(opts);
	},
	get: async (opts) => {
		_load();
		if (_capacitorHttp == null) {
			throw new Error('CapacitorHttp is not available in this runtime');
		}
		return _capacitorHttp.get(opts);
	},
};
